import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import './Dashboard.css';

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [psychotestStatus, setPsychotestStatus] = useState({ completed: false, result: null });
  const [showPsychotestResult, setShowPsychotestResult] = useState(false);
  const [loadingExams, setLoadingExams] = useState(true);
  const [startingId, setStartingId] = useState(null);
  const [error, setError] = useState('');

  // ─── Fetch daftar ujian ──────────────────────────────────────────────────────
  useEffect(() => {
    const fetchExams = async () => {
      try {
        const [examRes, psyRes] = await Promise.all([
          API.get('/exam/list'),
          API.get('/psychotest/status'),
        ]);
        setExams(examRes.data);
        setPsychotestStatus({
          completed: !!psyRes.data?.completed,
          result: psyRes.data?.result || null,
        });
        setShowPsychotestResult(false);
      } catch (err) {
        setError('Gagal memuat daftar ujian');
      } finally {
        setLoadingExams(false);
      }
    };
    fetchExams();
  }, []);

  // ─── Cek apakah ujian boleh dikerjakan ───────────────────────────────────────
  // Ujian ke-i boleh dikerjakan HANYA jika semua ujian sebelumnya (0..i-1) sudah 'finished'
  // Jika ada ujian sebelumnya yang 'failed' atau status lain, ujian terkunci
  const isExamUnlocked = (index) => {
    if (index === 0) return true; // ujian pertama selalu bisa
    const previousExams = exams.slice(0, index);
    return previousExams.every(prev => prev.session_status === 'finished');
  };

  // ─── Mulai / lanjut ujian ────────────────────────────────────────────────────
  const startExam = async (exam_id) => {
    if (!psychotestStatus.completed) {
      setError('Selesaikan psikotest terlebih dahulu sebelum mengerjakan exam.');
      return;
    }
    setStartingId(exam_id);
    setError('');
    try {
      const res = await API.post('/exam/start', { exam_id });
      navigate(`/exam/${res.data.session_id}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal memulai ujian');
      setStartingId(null);
    }
  };

  // ─── Format durasi ────────────────────────────────────────────────────────────
  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    if (minutes < 60) return `${minutes} Menit`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h} Jam ${m} Menit` : `${h} Jam`;
  };

  // ─── Status badge ujian ───────────────────────────────────────────────────────
  const getStatusBadge = (status) => {
    if (!status) return null;
    const map = {
      active:       { label: '▶ Sedang Berlangsung', cls: 'badge-active' },
      finished:     { label: '✅ Selesai',            cls: 'badge-finished' },
      failed:       { label: '❌ Tidak Lulus',        cls: 'badge-failed' },
      disqualified: { label: '🚫 Didiskualifikasi',   cls: 'badge-disq' },
    };
    const s = map[status];
    if (!s) return null;
    return <span className={`exam-badge ${s.cls}`}>{s.label}</span>;
  };

  // ─── Label tombol ─────────────────────────────────────────────────────────────
  const getButtonLabel = (exam, index, isStarting) => {
    if (isStarting) return 'Memuat...';
    if (!isExamUnlocked(index)) {
      // Cek apakah ada ujian sebelumnya yang failed
      const hasPreviousFailed = exams.slice(0, index).some(prev => prev.session_status === 'failed');
      if (hasPreviousFailed) {
        return '❌ Anda Gagal di Ujian Sebelumnya';
      }
      return '🔒 Selesaikan Ujian Sebelumnya';
    }
    if (exam.session_status === 'active')       return '▶ Lanjutkan Ujian';
    if (exam.session_status === 'finished' || exam.session_status === 'failed') return '📄 Lihat Hasil';
    if (exam.session_status === 'disqualified') return '🚫 Didiskualifikasi';
    return '🚀 Mulai Ujian';
  };

  const handleButton = (exam, index) => {
    if (!isExamUnlocked(index)) return; // sudah disabled, tapi double-check

    if (!psychotestStatus.completed) {
      setError('Selesaikan psikotest terlebih dahulu sebelum mengerjakan exam.');
      return;
    }

    if (['finished','failed','disqualified'].includes(exam.session_status)) {
      navigate(`/result/${exam.session_id}`);
      return;
    }
    startExam(exam.id);
  };

  return (
    <div className="dashboard">
      <header className="dash-header">
        <div className="dash-logo">📋 Sistem Ujian Rekrutmen</div>
        <div className="dash-user">
          <span>👤 {user?.name}</span>
          <button onClick={logout} className="btn-logout">Keluar</button>
        </div>
      </header>

      <main className="dash-main">
        <div className="welcome-card">
          <h1>Selamat Datang, {user?.name}!</h1>
          <p>Anda terdaftar sebagai peserta ujian rekrutmen.</p>
        </div>

        {error && <div className="alert-error">{error}</div>}

        {loadingExams ? (
          <div className="loading-screen">
            <div className="spinner"></div>
            <p>Memuat daftar ujian...</p>
          </div>
        ) : exams.length === 0 ? (
          <div className="empty-state">
            <p>Tidak ada ujian yang tersedia saat ini.</p>
          </div>
        ) : (
          <div className="exam-list">
            <div className={`exam-card psychotest-card ${psychotestStatus.completed ? 'psychotest-completed' : ''}`}>
              <div className="exam-card-header">
                <div className="exam-icon">🧠</div>
                <div className="exam-card-title">
                  <h2>Psikotest Pra-Exam</h2>
                  <span className={`exam-badge ${psychotestStatus.completed ? 'badge-finished' : 'badge-active'}`}>
                    {psychotestStatus.completed ? '✅ Selesai' : '⏳ Wajib Dikerjakan'}
                  </span>
                </div>
              </div>

              <p className="exam-desc">
                {psychotestStatus.completed
                  ? 'Psikotest sudah selesai dan hasil sudah direkam.'
                  : 'Selesaikan psikotest terlebih dahulu sebelum mengerjakan exam.'}
              </p>

              <div className="exam-info">
                <div className="info-item">
                  <span className="info-label">📌 Status</span>
                  <span className="info-val">{psychotestStatus.completed ? 'Selesai' : 'Belum Selesai'}</span>
                </div>
              </div>

              {psychotestStatus.completed && showPsychotestResult && (psychotestStatus.result?.dimensions || []).length > 0 && (
                <div className="rules-box">
                  <h3>📊 Ringkasan Hasil Psikotest</h3>
                  <ul>
                    {(psychotestStatus.result?.dimensions || []).map((d) => (
                      <li key={d.key}>{d.title}: {d.tendency} ({d.score}%)</li>
                    ))}
                  </ul>
                </div>
              )}

              {psychotestStatus.completed && showPsychotestResult && (psychotestStatus.result?.dimensions || []).length === 0 && (
                <div className="rules-box">
                  <h3>📊 Ringkasan Hasil Psikotest</h3>
                  <p>Hasil psikotest belum tersedia.</p>
                </div>
              )}

              <button
                className={`btn-start ${psychotestStatus.completed ? 'btn-secondary-start' : ''}`}
                style={{ maxWidth: '360px' }}
                onClick={() => {
                  if (psychotestStatus.completed) {
                    setShowPsychotestResult(prev => !prev);
                  } else {
                    navigate('/psychotest');
                  }
                }}
              >
                {psychotestStatus.completed
                  ? (showPsychotestResult ? 'Sembunyikan Hasil Psikotest' : 'Lihat Hasil Psikotest')
                  : 'Mulai Psikotest'}
              </button>
            </div>

            {exams.map((exam, index) => {
              const unlocked = isExamUnlocked(index);
              const isStarting = startingId === exam.id;
              const isBlocked = !unlocked || !psychotestStatus.completed;

              return (
                <div
                  className={`exam-card ${isBlocked ? 'exam-card-locked' : ''}`}
                  key={exam.id}
                >
                  {/* Lock overlay jika belum boleh dikerjakan */}
                  {isBlocked && (
                    <div className="lock-overlay">
                      <span className="lock-icon">🔒</span>
                      <p>{!psychotestStatus.completed ? 'Selesaikan psikotest terlebih dahulu' : 'Selesaikan ujian sebelumnya terlebih dahulu'}</p>
                    </div>
                  )}

                  <div className="exam-card-header">
                    <div className="exam-icon">{isBlocked ? '🔒' : '📝'}</div>
                    <div className="exam-card-title">
                      <h2>{exam.title}</h2>
                      {getStatusBadge(exam.session_status)}
                    </div>
                  </div>

                  {exam.description && (
                    <p className="exam-desc">{exam.description}</p>
                  )}

                  <div className="exam-info">
                    <div className="info-item">
                      <span className="info-label">⏱ Durasi</span>
                      <span className="info-val">{formatDuration(exam.duration_minutes)}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">❓ Soal</span>
                      <span className="info-val">{exam.total_questions} Pertanyaan</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">⚠️ Maks. Pelanggaran</span>
                      <span className="info-val">{exam.max_violations} Kali</span>
                    </div>
                  </div>

                  <div className="rules-box">
                    <h3>⚠️ Peraturan Ujian</h3>
                    <ul>
                      <li>Ujian wajib dilakukan dalam mode layar penuh</li>
                      <li>Dilarang berpindah tab atau keluar jendela browser</li>
                      <li>Kamera wajah harus aktif selama ujian</li>
                      <li>Wajah harus terdeteksi — jika hilang &gt;5 detik = pelanggaran</li>
                      <li>Lebih dari <strong>{exam.max_violations} pelanggaran</strong> akan didiskualifikasi</li>
                      <li>Copy-paste dinonaktifkan selama ujian</li>
                      <li>Jawaban tersimpan otomatis</li>
                    </ul>
                  </div>

                  <button
                    className={`btn-start ${!unlocked ? 'btn-locked' : ''} ${exam.session_status === 'disqualified' ? 'btn-disq' : ''}`}
                    onClick={() => handleButton(exam, index)}
                    disabled={
                      isStarting ||
                      !unlocked ||
                      !psychotestStatus.completed ||
                      exam.session_status === 'disqualified'
                    }
                  >
                    {getButtonLabel(exam, index, isStarting)}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}